import { AlertTriangle, CheckCircle2, HelpCircle, PackagePlus } from 'lucide-react';

// One product-bound plan (backend services/inventory_replenishment.py). Read-only; nothing here orders or transfers.
export const PLAN_TONE = {
  advice: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  ok: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  no_box_fits: 'bg-slate-100 text-slate-700 border-slate-200',
  utb_no_box: 'bg-amber-50 text-amber-800 border-amber-200',
  above_max: 'bg-slate-100 text-slate-600 border-slate-200',
  check_settings: 'bg-orange-50 text-orange-800 border-orange-200',
  check_code: 'bg-orange-50 text-orange-800 border-orange-200',
  unknown_stock: 'bg-gray-100 text-gray-500 border-gray-200',
  inbound_unknown: 'bg-amber-50 text-amber-900 border-amber-200',
};

const ICON = {
  advice: PackagePlus, ok: CheckCircle2, check_settings: AlertTriangle, check_code: AlertTriangle,
  utb_no_box: AlertTriangle, unknown_stock: HelpCircle, inbound_unknown: HelpCircle, no_box_fits: HelpCircle, above_max: AlertTriangle,
};

export function planTitle(plan) {
  if (!plan) return '';
  const s = plan.settings || {};
  const parts = [plan.reason, `min ${s.min ?? '—'} / max ${s.max ?? '—'} / doos ${s.p ?? '—'}`, plan.basisNote, plan.inboundNote];
  if (plan.minShortfall > 0) parts.push(`tekort tot minimum: ${plan.minShortfall}`);
  return parts.filter(Boolean).join(' · ');
}

export function UtbCodeCell({ code }) {
  if (!code) return <span className="text-gray-300">—</span>;
  if (code.status === 'ok') {
    return (
      <span className="font-mono text-xs text-gray-800" title={code.source === 'mapping' ? 'Bron: bestaande mapping (AA-code leeg) — bevestigen in AA' : code.source === 'aa+mapping' ? 'AA SupplierProductCode = bestaande mapping' : 'Bron: AA SupplierProductCode'}>
        {code.code}{code.source === 'mapping' && <span className="ml-1 text-[10px] text-amber-700">mapping</span>}
      </span>
    );
  }
  if (code.status === 'candidate') {
    return <span className="text-xs text-orange-800 font-semibold" title={`AA-code leeg na import; mapping-kandidaat ${(code.mappingCodes || []).join(', ')} is onbevestigd — bevestigen in AA. Blokkeert de bestelregel.`}>kandidaat {(code.mappingCodes || []).join(', ')}</span>;
  }
  if (code.status === 'conflict') {
    return <span className="text-xs text-orange-800 font-semibold" title={`AA: ${code.aaCode || '—'} · mapping: ${(code.mappingCodes || []).join(', ')} — niets automatisch vervangen`}>conflict</span>;
  }
  return <span className="text-xs text-orange-800" title="Geen UTB-code in AA en geen actieve mapping — eerst controleren">ontbreekt</span>;
}

export function PlanBadge({ plan, compact = false }) {
  if (!plan) return <span className="text-gray-300">—</span>;
  const Icon = ICON[plan.status] || HelpCircle;
  const s = plan.settings || {};
  const text = plan.status === 'advice'
    ? `${plan.boxes} ${plan.boxes === 1 ? 'doos' : 'dozen'} × ${s.p} = ${plan.qty}`
    : plan.label;
  return (
    <span data-testid={`plan-${plan.status}`} title={planTitle(plan)} aria-label={planTitle(plan)}
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${PLAN_TONE[plan.status] || PLAN_TONE.unknown_stock}`}>
      <Icon className="w-3 h-3 shrink-0" />
      <span className={compact ? 'truncate max-w-[180px]' : ''}>{text}</span>
      {plan.status === 'advice' && !compact && <span className="text-[10px] opacity-75" title="eind fysiek = fysiek aanwezig + verwacht + voorstel (≤ max) · eind beschikbaar = beschikbaar + verwacht + voorstel">→ eind fysiek {plan.endPosition} · beschikbaar {plan.endAvailable}</span>}
    </span>
  );
}

export function ExpectedCell({ plan, compact = false }) {
  const e = plan?.aaExpected;
  if (!e) return null;
  const when = e.observedAt ? ` · gelezen ${e.observedAt.slice(11, 16)} UTC` : '';
  const title = e.known
    ? `Verwacht bij AA (Maya 'Verwacht', aangemeld na UTB-bevestiging): ${e.value}${when}. Geen bewijs van fysiek transport, geen ETA; zegt niets over UTB-bestellingen die nog niet in Maya staan.`
    : `Verwacht bij AA onbekend — ${e.note || 'geen bron'}. Geen hoeveelheid vrijgegeven.`;
  return (
    <span data-testid={`aa-expected-${e.known ? 'known' : 'unknown'}`} title={title}
      className={`font-mono text-xs ${e.known ? (e.value > 0 ? 'text-indigo-700 font-semibold' : 'text-gray-500') : 'text-amber-700'}`}>
      {compact ? 'verw.' : 'Verwacht bij AA'} {e.known ? e.value : '?'}
    </span>
  );
}

export function ExpectedSummary({ aaExpected }) {
  if (!aaExpected) return null;
  const when = aaExpected.observedAt ? aaExpected.observedAt.slice(11, 16) + ' UTC' : '—';
  return (
    <span data-testid="aa-expected-summary" className={`text-xs ${aaExpected.ok ? 'text-gray-600' : 'text-amber-700 font-semibold'}`}>
      {aaExpected.ok
        ? `Verwacht bij AA gelezen (${when}): ${aaExpected.knownCount} bekend · ${aaExpected.positiveCount} > 0 · ${aaExpected.unknownCount} onbekend`
        : `Verwacht bij AA niet gelezen (${aaExpected.message || 'geen bron'}) — geen hoeveelheden vrijgegeven`}
    </span>
  );
}

export function BasisCell({ plan }) {
  const b = plan?.aaLevels;
  if (!b) return null;
  if (!b.known) return <span data-testid="aa-basis-unknown" className="font-mono text-xs text-amber-700" title={plan.basisNote}>AA beschikbaar/fysiek ?</span>;
  const diff = b.physical !== b.available;
  return (
    <span data-testid="aa-basis-known" className="font-mono text-xs text-gray-600" title={plan.basisNote}>
      AA beschikbaar <span className="text-gray-900 font-semibold">{b.available}</span> · fysiek <span className={diff ? 'text-gray-900 font-semibold' : ''}>{b.physical}</span>
      {diff && <span className="text-[10px] text-gray-500"> (gereserveerd {b.ordered ?? '?'}, vervallen {b.expired ?? '?'}, quarantaine {b.quarantined ?? '?'})</span>}
    </span>
  );
}

export function BasisCompact({ plan }) {
  const b = plan?.aaLevels;
  if (!b) return null;
  if (!b.known) return <span data-testid="aa-basis-unknown" className="font-mono text-[10px] text-amber-700" title={plan.basisNote}>besch./fys. ?</span>;
  return <span data-testid="aa-basis-known" className="font-mono text-[10px] text-gray-500" title={plan.basisNote}>besch. {b.available} · fys. {b.physical}</span>;
}

export function SettingsCell({ plan }) {
  const s = plan?.settings;
  if (!s) return <span className="text-gray-300">—</span>;
  const bad = s.flags && s.flags.length > 0;
  return (
    <span className={`font-mono text-xs ${bad ? 'text-orange-800' : 'text-gray-600'}`} title={bad ? `Instellingen controleren: ${s.flags.join('; ')}` : 'AA StockMinimum / StockMaximum · doos = AA OrderQuantity'}>
      {s.min ?? '—'}/{s.max ?? '—'} <span className="text-gray-400">·</span> {s.p ?? '—'}{bad && <AlertTriangle className="inline w-3 h-3 ml-1 text-orange-600" />}
    </span>
  );
}
